import { useMutation, useQuery } from '@tanstack/react-query';
import { APITypes } from '../../primary-record-types';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import {
    getCareTeamMemberCollaboratorsCacheKey,
    onCollaboratorUpdate,
    getProfileCollaboratorInvitesCacheKey,
    getProfileCollaboratorsCacheKey,
} from './cacheKeys';
import { useQueryErrorNotify } from '../useQueryCallbacks';
import { isOrganizationCollaborator, isUserCollaborator } from '../../utils/collaboratorUtils';
import useRecordEvent from '../useRecordEvent';
import { useHasPermission } from '../../components/v2BaseComponents/Permissions/hooks';

const intl = getIntl('useCollaborators', {
    collaboratorsLoadFailure: 'Failed to load collaborators',
    inviteAlreadyExists: 'Invite already exists',
    alreadyCollaborator: 'User already has access to this profile',
    inviteSuccess: 'Invite successfully sent',
    inviteFailure: 'Failed to send invite',
    collaboratorLoadFailure: 'Failed to load collaborators',
    collaboratorDeleteSuccess: 'Collaborator successfully removed',
    collaboratorDeleteFailure: 'Failed to remove collaborator',
    deleteInvitationSuccess: 'Invitation successfully revoked',
    deleteInvitationFailure: 'Failed to revoke invitation',
    inviteLoadFailure: 'Failed to load invites',
    updateInvitePermissionsSuccess: 'Invite successfully updated',
    updateInvitePermissionsFailure: 'Failed to update invite',
    updateCollaboratorPermissionsSuccess: 'Collaborator successfully updated',
    updateCollaboratorPermissionsFailure: 'Failed to update collaborator',
    resendInviteSuccess: 'Invite successfully resent',
    resendInviteFailure: 'Failed to resend invite',
});

export type UseCollaboratorsArgs = {
    medicalProfileId: string | null;
    careTeamMemberId?: string;
    disableInviteLoad?: boolean;
    onInviteSuccess?: () => void;
    onDeleteCollaboratorSuccess?: () => void;
    onDeleteInviteSuccess?: () => void;
    onUpdateInviteSuccess?: () => void;
    onUpdateCollaboratorPermissionsSuccess?: () => void;
};

export type UpdateCollaboratorInvitePermissionsArgs = {
    update: APITypes.medicalProfileService.UpdatePermissionsRequest;
    inviteId: string;
};

export type UpdateCollaboratorPermissionsArgs = {
    update: APITypes.medicalProfileService.UpdatePermissionsRequest;
    collaboratorId: string;
};

export type UseCollaboratorsReturnValue = {
    collaborators: APITypes.medicalProfileService.MedicalProfileCollaborator[];
    userCollaborators: APITypes.medicalProfileService.MedicalProfileUserCollaborator[];
    organizationCollaborators: APITypes.medicalProfileService.MedicalProfileOrganizationCollaborator[];
    isLoadingCollaborators: boolean;
    careTeamCollaborators: APITypes.medicalProfileService.CareTeamCollaborator[];
    isLoadingCareTeamCollaborators: boolean;
    inviteCollaborator: (body: APITypes.medicalProfileService.MedicalProfileCollaboratorInviteBody) => void;
    isInvitingCollaborator: boolean;
    deleteCollaborator: (collaboratorId: string) => void;
    isDeletingCollaborator: boolean;
    collaboratorInvites: APITypes.medicalProfileService.CollaboratorInviteListItem[];
    isLoadingCollaboratorInvites: boolean;
    deleteCollaboratorInvite: (collaboratorInviteId: string) => void;
    isDeletingCollaboratorInvite: boolean;
    updateCollaboratorInvite: (args: UpdateCollaboratorInvitePermissionsArgs) => void;
    isUpdatingCollaboratorInvite: boolean;
    updateCollaboratorPermissions: (args: UpdateCollaboratorPermissionsArgs) => void;
    isUpdatingCollaboratorPermissions: boolean;
    resendInvite: (inviteId: string) => void;
    isResendingInvite: boolean;
};

export const useCollaborators = ({
    disableInviteLoad,
    medicalProfileId,
    careTeamMemberId,
    onInviteSuccess,
    onDeleteCollaboratorSuccess,
    onDeleteInviteSuccess,
    onUpdateInviteSuccess,
    onUpdateCollaboratorPermissionsSuccess,
}: UseCollaboratorsArgs): UseCollaboratorsReturnValue => {
    const resetCollaborators = onCollaboratorUpdate.resetCache({ medicalProfileId: medicalProfileId! });
    const notify = useNotify();
    const hasViewPermission = useHasPermission(medicalProfileId, 'collaborators', 'view');

    const collaboratorsQuery = useQuery({
        queryKey: getProfileCollaboratorsCacheKey(medicalProfileId!),
        queryFn: async () => {
            return (await appAPI.get(
                urls.api.collaborators.collaborators(medicalProfileId!),
            )) as APITypes.medicalProfileService.ListMedicalProfileCollaboratorsResponse;
        },
        enabled: !!medicalProfileId && hasViewPermission && !!medicalProfileId,
    });
    const record = useRecordEvent();
    useQueryErrorNotify(collaboratorsQuery, intl('collaboratorLoadFailure'));

    const careTeamCollaboratorsQuery = useQuery({
        queryKey: getCareTeamMemberCollaboratorsCacheKey(medicalProfileId!, careTeamMemberId || ''),
        queryFn: async () => {
            return appAPI.get<APITypes.medicalProfileService.ListCareTeamCollaboratorsResponse>(
                urls.api.collaborators.careTeamMemberCollaborators(medicalProfileId!, careTeamMemberId!),
            );
        },
        enabled: !!medicalProfileId && !!careTeamMemberId && !!medicalProfileId,
    });
    useQueryErrorNotify(careTeamCollaboratorsQuery, intl('collaboratorLoadFailure'));

    const { mutateAsync: inviteCollaborator, isPending: isInvitingCollaborator } = useMutation<
        APITypes.medicalProfileService.InviteResponse,
        any,
        APITypes.medicalProfileService.MedicalProfileCollaboratorInviteBody
    >({
        mutationFn: async (body: APITypes.medicalProfileService.MedicalProfileCollaboratorInviteBody) => {
            if (!medicalProfileId) {
                throw new Error('No medical profile id provided');
            }

            const response = (await appAPI.post(urls.api.collaborators.medicalProfileInvites(medicalProfileId), {
                body,
            })) as APITypes.medicalProfileService.InviteResponse;
            return response;
        },
        onSuccess: (response) => {
            if (response.status === 'invite-exists') {
                notify('error', intl('inviteAlreadyExists'));
            } else if (response.status === 'collaborator-exists') {
                notify('error', intl('alreadyCollaborator'));
            } else {
                resetCollaborators();
                notify('success', intl('inviteSuccess'));
                onInviteSuccess?.();
            }
        },
        onError: () => {
            notify('error', intl('inviteFailure'));
        },
    });

    const { mutateAsync: deleteCollaborator, isPending: isDeletingCollaborator } = useMutation({
        mutationFn: async (collaboratorId: string) => {
            if (!medicalProfileId) {
                throw new Error('No medical profile id provided');
            }

            await appAPI.del(urls.api.collaborators.collaborator(medicalProfileId, collaboratorId));
        },
        onSuccess: () => {
            resetCollaborators();
            record('deleteCollaborator');
            notify('success', intl('collaboratorDeleteSuccess'));
            onDeleteCollaboratorSuccess?.();
        },
        onError: () => {
            notify('error', intl('collaboratorDeleteFailure'));
        },
    });

    const profileCollaboratorInvitesQuery = useQuery({
        queryKey: getProfileCollaboratorInvitesCacheKey(medicalProfileId!),
        queryFn: async () => {
            const response = (await appAPI.get(
                urls.api.collaborators.medicalProfileInvites!(medicalProfileId!),
            )) as APITypes.medicalProfileService.ListCollaboratorInvitesResponse;
            return response.invites;
        },
        enabled: !disableInviteLoad && !!medicalProfileId && hasViewPermission,
    });
    useQueryErrorNotify(profileCollaboratorInvitesQuery, intl('inviteLoadFailure'));

    const { mutate: deleteCollaboratorInvite, isPending: isDeletingCollaboratorInvite } = useMutation({
        mutationFn: async (collaboratorInviteId: string) => {
            if (!medicalProfileId) {
                throw new Error('No medical profile id provided');
            }

            await appAPI.del(urls.api.collaborators.medicalProfileInvite(medicalProfileId, collaboratorInviteId));
        },
        onSuccess: () => {
            resetCollaborators();
            notify('success', intl('deleteInvitationSuccess'));
            onDeleteInviteSuccess?.();
        },
        onError: () => {
            notify('error', intl('deleteInvitationFailure'));
        },
    });

    const { mutateAsync: updateCollaboratorInvite, isPending: isUpdatingCollaboratorInvite } = useMutation({
        mutationFn: async (args: UpdateCollaboratorInvitePermissionsArgs) => {
            if (!medicalProfileId) {
                throw new Error('No medical profile id provided');
            }

            await appAPI.put(urls.api.collaborators.medicalProfileInvitePermissions(medicalProfileId, args.inviteId), {
                body: args.update,
            });
        },
        onSuccess: () => {
            resetCollaborators();
            notify('success', intl('updateInvitePermissionsSuccess'));
            onUpdateInviteSuccess?.();
        },
        onError: () => {
            notify('error', intl('updateInvitePermissionsFailure'));
        },
    });

    const { mutateAsync: updateCollaboratorPermissions, isPending: isUpdatingCollaboratorPermissions } = useMutation({
        mutationFn: async (args: UpdateCollaboratorPermissionsArgs) => {
            if (!medicalProfileId) {
                throw new Error('No medical profile id provided');
            }

            await appAPI.put(urls.api.collaborators.collaboratorPermissions(medicalProfileId, args.collaboratorId), {
                body: args.update,
            });
        },
        onSuccess: () => {
            resetCollaborators();
            notify('success', intl('updateCollaboratorPermissionsSuccess'));
            onUpdateCollaboratorPermissionsSuccess?.();
        },
        onError: () => {
            notify('error', intl('updateInvitePermissionsFailure'));
        },
    });

    const { mutateAsync: resendInvite, isPending: isResendingInvite } = useMutation({
        mutationFn: async (inviteId: string) => {
            if (!medicalProfileId) {
                throw new Error('No medical profile id provided');
            }

            await appAPI.post(urls.api.collaborators.resendInviteEmail(medicalProfileId, inviteId), {});
        },
        onSuccess: () => {
            notify('success', intl('resendInviteSuccess'));
        },
        onError: () => {
            notify('error', intl('resendInviteFailure'));
        },
    });

    const collaborators = collaboratorsQuery.data?.collaborators || [];

    return {
        collaborators,
        userCollaborators: collaborators.filter(isUserCollaborator),
        organizationCollaborators: collaborators.filter(isOrganizationCollaborator),
        isLoadingCollaborators: collaboratorsQuery.isLoading,
        careTeamCollaborators: careTeamCollaboratorsQuery.data?.collaborators || [],
        isLoadingCareTeamCollaborators: careTeamCollaboratorsQuery.isLoading,
        inviteCollaborator,
        isInvitingCollaborator,
        deleteCollaborator: deleteCollaborator,
        isDeletingCollaborator,
        collaboratorInvites: profileCollaboratorInvitesQuery.data || [],
        isLoadingCollaboratorInvites: profileCollaboratorInvitesQuery.isLoading,
        deleteCollaboratorInvite,
        isDeletingCollaboratorInvite,
        updateCollaboratorInvite,
        isUpdatingCollaboratorInvite,
        updateCollaboratorPermissions,
        isUpdatingCollaboratorPermissions,
        resendInvite,
        isResendingInvite,
    };
};
